import React, { useMemo, useState } from 'react';
import {
  Plus,
  Minus,
  X,
  ShoppingCart,
  Search,
  UserRound,
  CheckCircle2,
  Snowflake,
  Sparkles,
  Activity,
  Package as PackageIcon,
  DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  SERVICES,
  PACKAGE_TIERS,
  calculateCart,
  money,
  moneyRange,
  clampPrice,
} from '../../lib/services';
import {
  saveSale,
  upsertPackage,
  generateId,
} from '../../lib/salesStorage';

const CATEGORY_ICONS = {
  aesthetic: Sparkles,
  orthopedic: Activity,
};

export default function POSTab({ waivers, onSaleCreated }) {
  // Cart lines: { id, serviceId, sessions, unitPrice }
  const [cart, setCart] = useState([]);
  const [client, setClient] = useState(null);
  const [clientQuery, setClientQuery] = useState('');
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [walkInName, setWalkInName] = useState('');

  const calc = useMemo(() => calculateCart(cart), [cart]);

  const addToCart = (serviceId, sessions = 1) => {
    const svc = SERVICES.find((s) => s.id === serviceId);
    if (!svc) return;
    setCart((c) => [
      ...c,
      { id: generateId('l'), serviceId, sessions, unitPrice: svc.priceMin },
    ]);
  };

  const updateLine = (id, patch) => {
    setCart((c) =>
      c.map((l) => {
        if (l.id !== id) return l;
        const next = { ...l, ...patch };
        if (patch.unitPrice != null) {
          const svc = SERVICES.find((s) => s.id === next.serviceId);
          next.unitPrice = clampPrice(svc, patch.unitPrice);
        }
        return next;
      })
    );
  };

  const removeLine = (id) => setCart((c) => c.filter((l) => l.id !== id));

  const clearCart = () => {
    setCart([]);
    setClient(null);
    setWalkInName('');
  };

  const filteredClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return waivers.slice(0, 20);
    return waivers
      .filter((w) =>
        [w.name, w.email, w.phone].some((f) =>
          String(f || '').toLowerCase().includes(q)
        )
      )
      .slice(0, 20);
  }, [waivers, clientQuery]);

  const canComplete =
    cart.length > 0 && (client || walkInName.trim().length >= 2);

  const completeSale = () => {
    if (!canComplete) return;

    const clientId = client?.id || null;
    const clientName = client?.name || walkInName.trim();
    const now = new Date().toISOString();
    const saleId = generateId('s');

    const items = calc.lines.map((l) => ({
      lineId: l.id,
      serviceId: l.serviceId,
      serviceName: `${l.service.name}${l.service.subtitle ? ` (${l.service.subtitle})` : ''}`,
      sessions: l.sessions,
      unitPrice: l.unitPrice,
      tier: {
        sessions: l.tier.sessions,
        discountPct: l.tier.discountPct,
        label: l.tier.label,
      },
      lineSubtotal: l.lineStandardSubtotal,
      lineDiscount: l.lineDiscount,
      lineTotal: l.lineTotal,
    }));

    const sale = {
      id: saleId,
      clientId,
      clientName,
      isWalkIn: !clientId,
      items,
      subtotal: calc.subtotal,
      packageDiscount: calc.packageDiscount,
      total: calc.total,
      createdAt: now,
    };
    saveSale(sale);

    calc.lines
      .filter((l) => l.sessions > 1)
      .forEach((l) => {
        const pkg = {
          id: generateId('p'),
          saleId,
          clientId,
          clientName,
          serviceId: l.serviceId,
          serviceName: `${l.service.name}${l.service.subtitle ? ` (${l.service.subtitle})` : ''}`,
          sessionsPurchased: l.sessions,
          sessionsRemaining: l.sessions,
          unitPrice: l.unitPrice,
          discountPct: l.tier.discountPct,
          totalPaid: l.lineTotal,
          createdAt: now,
          redemptions: [],
        };
        upsertPackage(pkg);
      });

    toast.success(`Sale complete — ${money(calc.total)}`);
    clearCart();
    onSaleCreated?.();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: Service catalog */}
      <div className="lg:col-span-2 space-y-6">
        <ServiceGroup
          title="Aesthetics"
          icon={Sparkles}
          services={SERVICES.filter((s) => s.category === 'aesthetic')}
          onAdd={addToCart}
        />
        <ServiceGroup
          title="Orthopedic"
          icon={Activity}
          services={SERVICES.filter((s) => s.category === 'orthopedic')}
          onAdd={addToCart}
        />
      </div>

      {/* Right: Cart */}
      <div className="lg:col-span-1">
        <div className="bg-slate-900 rounded-2xl border border-white/10 sticky top-24 overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-blue-400" />
              <p className="font-display text-lg text-white">Sale</p>
              <span className="text-xs text-slate-400">({cart.length})</span>
            </div>
            {cart.length > 0 && (
              <button
                onClick={clearCart}
                className="text-xs text-slate-400 hover:text-red-400 font-semibold"
                data-testid="pos-clear-cart"
              >
                Clear
              </button>
            )}
          </div>

          <div className="p-4 max-h-[480px] overflow-y-auto" data-testid="pos-cart-list">
            {cart.length === 0 && (
              <div className="text-center py-10 text-slate-500 text-sm">
                Add a service to get started.
              </div>
            )}
            {calc.lines.map((l) => (
              <CartLine
                key={l.id}
                line={l}
                onUpdate={(patch) => updateLine(l.id, patch)}
                onRemove={() => removeLine(l.id)}
              />
            ))}
          </div>

          {/* Client */}
          <div className="px-6 py-4 border-t border-white/10 bg-slate-950/50">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-slate-400 mb-2">
              Client
            </p>
            {client ? (
              <div
                className="p-3 rounded-xl bg-slate-900 border border-blue-500/30 flex items-center justify-between"
                data-testid="pos-client-selected"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">
                    {client.name}
                  </p>
                  <p className="text-xs text-slate-400 truncate">
                    {client.email} · {client.phone}
                  </p>
                </div>
                <button
                  onClick={() => setClient(null)}
                  className="text-slate-500 hover:text-red-400 shrink-0"
                  data-testid="pos-client-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => setShowClientPicker(true)}
                  className="w-full h-11 rounded-xl bg-slate-800 border border-white/10 hover:border-blue-400/50 text-slate-200 text-sm font-semibold inline-flex items-center justify-center gap-2"
                  data-testid="pos-open-client-picker"
                >
                  <UserRound className="w-4 h-4" />
                  Choose signed client
                </button>
                <input
                  type="text"
                  value={walkInName}
                  onChange={(e) => setWalkInName(e.target.value)}
                  placeholder="or enter walk-in name"
                  className="glass-input w-full h-11 px-3.5 rounded-xl text-sm"
                  data-testid="pos-walkin-input"
                />
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="px-6 py-4 border-t border-white/10 space-y-1 text-sm">
            <Row label="Subtotal" value={money(calc.subtotal)} testId="pos-subtotal" />
            {calc.packageDiscount > 0 && (
              <Row
                label="Package discount"
                value={`− ${money(calc.packageDiscount)}`}
                accent
                testId="pos-package-discount"
              />
            )}
            <div className="flex items-baseline justify-between pt-3 border-t border-dashed border-white/10 mt-2">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-slate-400">Total</p>
              <p className="font-display text-3xl text-white" data-testid="pos-total">
                {money(calc.total)}
              </p>
            </div>
          </div>

          <div className="p-4 border-t border-white/10">
            <button
              onClick={completeSale}
              disabled={!canComplete}
              className="pill-btn w-full h-14 rounded-full bg-blue-500 hover:bg-blue-400 text-white font-bold shadow-[0_0_24px_-4px_rgba(0,168,255,0.7)] inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
              data-testid="pos-complete-sale-button"
            >
              <CheckCircle2 className="w-5 h-5" />
              Complete Sale
            </button>
          </div>
        </div>
      </div>

      {/* Client picker modal */}
      {showClientPicker && (
        <div
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowClientPicker(false)}
          data-testid="pos-client-picker-modal"
        >
          <div
            className="w-full max-w-lg glass-card rounded-3xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <p className="font-display text-lg text-white">Select Client</p>
              <button
                onClick={() => setShowClientPicker(false)}
                className="text-slate-400 hover:text-white"
                data-testid="pos-client-picker-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  autoFocus
                  type="text"
                  value={clientQuery}
                  onChange={(e) => setClientQuery(e.target.value)}
                  placeholder="Search by name, email, phone..."
                  className="glass-input w-full h-11 pl-10 pr-3 rounded-xl text-sm"
                  data-testid="pos-client-picker-search"
                />
              </div>
              <div className="mt-3 max-h-[50vh] overflow-y-auto space-y-1">
                {filteredClients.length === 0 && (
                  <p className="text-center text-slate-500 text-sm py-8">
                    {waivers.length === 0
                      ? 'No signed waivers yet.'
                      : 'No clients match your search.'}
                  </p>
                )}
                {filteredClients.map((w) => (
                  <button
                    key={w.id}
                    onClick={() => {
                      setClient(w);
                      setShowClientPicker(false);
                      setClientQuery('');
                    }}
                    className="w-full text-left px-4 py-3 rounded-xl hover:bg-white/5 border border-transparent hover:border-blue-500/30 transition"
                    data-testid={`pos-client-option-${w.id}`}
                  >
                    <p className="text-sm font-semibold text-white">{w.name}</p>
                    <p className="text-xs text-slate-400">
                      {w.email} · {w.phone}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceGroup({ title, icon: Icon, services, onAdd }) {
  return (
    <div>
      <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-blue-400 mb-3 px-1 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" />
        {title}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.map((svc) => (
          <ServiceButton key={svc.id} svc={svc} onAdd={(sessions) => onAdd(svc.id, sessions)} />
        ))}
      </div>
    </div>
  );
}

function ServiceButton({ svc, onAdd }) {
  const [size, setSize] = useState(1);
  const Icon = CATEGORY_ICONS[svc.category] || Sparkles;
  const sizeOptions = [1, ...PACKAGE_TIERS.filter((t) => t.sessions > 1).map((t) => t.sessions)];
  const idx = sizeOptions.indexOf(size);
  return (
    <div
      className="p-5 rounded-2xl bg-slate-900 border border-white/10 hover:border-blue-400/50 transition"
      data-testid={`pos-service-${svc.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-blue-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white leading-tight">
              {svc.name}
            </p>
            <p className="text-xs text-slate-400">{svc.subtitle}</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-display text-blue-300 whitespace-nowrap">
            {moneyRange(svc)}
          </p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <div className="inline-flex items-center rounded-full border border-white/10 overflow-hidden">
          <button
            type="button"
            onClick={() => idx > 0 && setSize(sizeOptions[idx - 1])}
            className="w-9 h-9 flex items-center justify-center text-slate-300 hover:bg-white/5 disabled:opacity-30"
            disabled={idx <= 0}
            data-testid={`pos-service-size-dec-${svc.id}`}
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <span
            className="min-w-[42px] text-center text-sm font-bold text-white"
            data-testid={`pos-service-size-${svc.id}`}
          >
            ×{size}
          </span>
          <button
            type="button"
            onClick={() => idx < sizeOptions.length - 1 && setSize(sizeOptions[idx + 1])}
            className="w-9 h-9 flex items-center justify-center text-slate-300 hover:bg-white/5 disabled:opacity-30"
            disabled={idx >= sizeOptions.length - 1}
            data-testid={`pos-service-size-inc-${svc.id}`}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
        {size > 1 && (
          <span className="text-xs font-semibold text-blue-300 inline-flex items-center gap-1">
            <PackageIcon className="w-3 h-3" />
            Package
          </span>
        )}
        <button
          type="button"
          onClick={() => onAdd(size)}
          className="ml-auto pill-btn inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold shadow-[0_0_16px_-4px_rgba(0,168,255,0.6)]"
          data-testid={`pos-service-add-${svc.id}`}
        >
          <Plus className="w-3.5 h-3.5" />
          Add
        </button>
      </div>
    </div>
  );
}

function CartLine({ line, onUpdate, onRemove }) {
  const l = line;
  return (
    <div
      className="p-4 rounded-2xl border border-white/10 hover:border-blue-500/30 transition mb-3"
      data-testid={`pos-cart-line-${l.serviceId}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">
            {l.service.name}
          </p>
          <p className="text-xs text-slate-400">{l.service.subtitle}</p>
        </div>
        <button
          onClick={onRemove}
          className="text-slate-500 hover:text-red-400 shrink-0"
          data-testid={`pos-cart-remove-${l.id}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Price picker within range */}
      <div className="mt-3">
        <div className="flex items-baseline justify-between text-[11px] tracking-widest uppercase font-bold text-slate-400 mb-1">
          <span>Unit price</span>
          <span className="text-slate-500 normal-case tracking-normal">
            {moneyRange(l.service)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
            <input
              type="number"
              inputMode="decimal"
              min={l.service.priceMin}
              max={l.service.priceMax}
              step="1"
              value={l.unitPrice}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (Number.isNaN(v)) return;
                onUpdate({ unitPrice: v });
              }}
              className="glass-input w-full h-9 pl-7 pr-2 rounded-lg text-sm font-semibold"
              data-testid={`pos-line-price-${l.id}`}
            />
          </div>
          <input
            type="range"
            min={l.service.priceMin}
            max={l.service.priceMax}
            step="1"
            value={l.unitPrice}
            onChange={(e) => onUpdate({ unitPrice: parseFloat(e.target.value) })}
            className="flex-1 accent-blue-500"
            data-testid={`pos-line-price-slider-${l.id}`}
          />
        </div>
      </div>

      {/* Package tier selector */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {PACKAGE_TIERS.map((t) => (
          <button
            key={t.sessions}
            onClick={() => onUpdate({ sessions: t.sessions })}
            className={`text-xs h-8 px-2.5 rounded-full font-semibold border transition ${
              l.sessions === t.sessions
                ? 'bg-blue-500 text-white border-blue-500 shadow-[0_0_12px_-4px_rgba(0,168,255,0.6)]'
                : 'bg-slate-900 text-slate-300 border-white/10 hover:border-blue-400/50'
            }`}
            data-testid={`pos-line-tier-${l.id}-${t.sessions}`}
          >
            {t.sessions === 1 ? '×1' : `×${t.sessions} · -${t.discountPct}%`}
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-end justify-between">
        <div className="text-xs text-slate-400">
          {l.sessions} × {money(l.unitPrice)}
          {l.tier.discountPct > 0 && (
            <span className="ml-1 text-blue-300">−{l.tier.discountPct}%</span>
          )}
        </div>
        <p
          className="text-lg font-display text-white"
          data-testid={`pos-line-total-${l.id}`}
        >
          {money(l.lineTotal)}
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, accent, testId }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-400">{label}</span>
      <span
        className={`font-semibold ${accent ? 'text-blue-300' : 'text-white'}`}
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}
